// ─── Activity Log ──────────────────────────────────────────
// Real-time event log for ChinMonitor order execution debugging.
// Shows chronological events: config checks, risk evaluations,
// order placements, errors, and loop state transitions.

import { useRef, useEffect } from 'react';

export interface LogEntry {
  id: number;
  timestamp: string;
  level: 'info' | 'success' | 'warn' | 'error';
  symbol: string;
  message: string;
}

interface ActivityLogProps {
  entries: LogEntry[];
  onClear: () => void;
  onClose: () => void;
}

const LEVEL_CONFIG: Record<LogEntry['level'], { icon: string; color: string; bg: string }> = {
  info:    { icon: 'ri-information-line',    color: 'text-foreground-500', bg: 'bg-background-200/60' },
  success: { icon: 'ri-check-line',          color: 'text-emerald-400',    bg: 'bg-emerald-500/10' },
  warn:    { icon: 'ri-error-warning-line',  color: 'text-amber-400',      bg: 'bg-amber-500/10' },
  error:   { icon: 'ri-close-circle-line',   color: 'text-red-400',        bg: 'bg-red-500/10' },
};

export default function ActivityLog({ entries, onClear, onClose }: ActivityLogProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest entry
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [entries.length]);

  return (
    <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-background-200/70 flex items-center justify-between bg-background-100">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-accent-500/10 flex items-center justify-center">
            <i className="ri-terminal-box-line text-accent-400 text-xs"></i>
          </div>
          <h4 className="text-xs font-heading font-semibold text-foreground-900">Activity Log</h4>
          <span className="text-[10px] text-foreground-400 bg-background-200/70 px-1.5 py-0.5 rounded-full font-mono">
            {entries.length}
          </span>
          {/* Live indicator */}
          <span className="inline-flex items-center gap-1 text-[9px] text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
            LIVE
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onClear}
            className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-200/70 transition-all cursor-pointer"
            title="Clear log"
          >
            <i className="ri-delete-bin-line text-[11px]"></i>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 hover:bg-background-200/70 transition-all cursor-pointer"
            title="Close"
          >
            <i className="ri-close-line text-xs"></i>
          </button>
        </div>
      </div>

      {/* Log entries */}
      <div className="max-h-[320px] overflow-y-auto">
        {entries.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <i className="ri-terminal-line text-foreground-300 text-2xl block mb-2"></i>
            <p className="text-xs text-foreground-400">No events yet. Set an Order direction to start logging.</p>
          </div>
        ) : (
          <div className="divide-y divide-background-200/30">
            {entries.map((entry) => {
              const cfg = LEVEL_CONFIG[entry.level];
              return (
                <div
                  key={entry.id}
                  className={['px-3 py-2 flex items-start gap-2.5 transition-colors hover:bg-background-100/50', cfg.bg].join(' ')}
                >
                  {/* Icon */}
                  <div className={['w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5', cfg.bg].join(' ')}>
                    <i className={[cfg.icon, cfg.color, 'text-[10px]'].join(' ')}></i>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={['text-[10px] font-semibold', cfg.color].join(' ')}>
                        {entry.symbol}
                      </span>
                      <span className="text-[9px] text-foreground-400 font-mono whitespace-nowrap">
                        {entry.timestamp}
                      </span>
                    </div>
                    <p className={['text-[11px] leading-relaxed break-words', entry.level === 'error' ? 'text-red-400' : 'text-foreground-600'].join(' ')}>
                      {entry.message}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef}></div>
          </div>
        )}
      </div>

      {/* Footer stats */}
      {entries.length > 0 && (
        <div className="px-4 py-1.5 border-t border-background-200/70 bg-background-100 flex items-center gap-3 text-[9px] text-foreground-400">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
            {entries.filter((e) => e.level === 'success').length} success
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-amber-400"></span>
            {entries.filter((e) => e.level === 'warn').length} warnings
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-400"></span>
            {entries.filter((e) => e.level === 'error').length} errors
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-foreground-300"></span>
            {entries.filter((e) => e.level === 'info').length} info
          </span>
        </div>
      )}
    </div>
  );
}